import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

// Import individual card components
import QuickCashCard from './CardTypes/QuickCashCard';
import CashKingCard from './CardTypes/CashKingCard';
import JackpotMillionsCard from './CardTypes/JackpotMillionsCard';
import MegaMillionsCard from './CardTypes/MegaMillionsCard';
import CustomGameCard from './CardTypes/CustomGameCard';

// Import standalone game components
import QuickCashGame from './Games/QuickCash/QuickCashGame';
import CashKingGame from './Games/CashKing/CashKingGame';
import JackpotMillionsGame from './Games/JackpotMillions/JackpotMillionsGame';
import MegaMillionsGame from './Games/MegaMillions/MegaMillionsGame';

/**
 * ScratchCardDisplay Component
 * 
 * A wrapper component that selects the appropriate card component
 * based on the cardType prop. This ensures consistent rendering
 * of each card type throughout the application.
 */
const ScratchCardDisplay = ({ 
  cardType, 
  isPreview = false,
  onPlay,
  onBuy,
  onReveal,
  gameState = null
}) => {
  // Get the current URL to check if we're on the Cash King page
  const location = useLocation();
  const currentPath = location.pathname;
  
  // State for custom games from localStorage
  const [customGames, setCustomGames] = useState([]);
  const [selectedCustomGame, setSelectedCustomGame] = useState(null);
  
  // Load custom games from localStorage
  useEffect(() => {
    try {
      // Force a fresh read from localStorage every time
      const rawData = localStorage.getItem('userCreatedGames');
      console.log('DEBUG ScratchCardDisplay - Raw localStorage data:', rawData);
      
      const savedGames = JSON.parse(rawData || '[]');
      console.log('DEBUG ScratchCardDisplay - Parsed custom games:', savedGames);
      
      if (savedGames && savedGames.length > 0) {
        setCustomGames(savedGames);
        console.log('DEBUG ScratchCardDisplay - Set customGames state with', savedGames.length, 'games');
        
        // If cardType looks like a custom game ID, find that game
        if (cardType && cardType.startsWith('game_')) {
          console.log('DEBUG ScratchCardDisplay - Looking for custom game with ID:', cardType);
          const game = savedGames.find(g => g.id === cardType);
          if (game) {
            console.log('DEBUG ScratchCardDisplay - Found custom game:', game.title);
            setSelectedCustomGame(game);
          } else {
            console.warn('DEBUG ScratchCardDisplay - Custom game not found with ID:', cardType);
          }
        }
      } else {
        console.warn('DEBUG ScratchCardDisplay - No custom games found in localStorage');
      }
    } catch (error) {
      console.error('Error loading custom games in ScratchCardDisplay:', error);
    }
  }, [cardType]);
  
  // Log the cardType and URL for debugging
  console.log('ScratchCardDisplay - cardType:', cardType, 'URL:', currentPath, 'isCustomGame:', !!selectedCustomGame);
  
  // Check if we're on the modular profile page with cardType parameter
  // Extract cardType from URL path (not from query parameters)
  const pathParts = currentPath.split('/');
  const lastPart = pathParts[pathParts.length - 1];
  const urlCardType = lastPart && lastPart.startsWith('game_') ? lastPart : null;
  const isModularProfile = currentPath.includes('/modular-profile');
  
  console.log('DEBUG ScratchCardDisplay - URL path parts:', pathParts);
  console.log('DEBUG ScratchCardDisplay - Last part of URL:', lastPart);
  console.log('DEBUG ScratchCardDisplay - Extracted urlCardType:', urlCardType);
  
  // If we're on the modular profile page with cardType in the URL path, handle accordingly
  if (isModularProfile && lastPart) {
    console.log('DEBUG ScratchCardDisplay - Processing URL path game type:', lastPart);
    
    // Handle predefined game types
    if (lastPart === 'cash_king') {
      console.log('ScratchCardDisplay - Using standalone CashKingGame from URL path');
      return (
        <CashKingGame 
          isPreview={false}
          gameState={gameState}
          onClose={onPlay} // Use onPlay as the onClose handler
        />
      );
    }
    
    if (lastPart === 'quick_cash') {
      console.log('ScratchCardDisplay - Using standalone QuickCashGame from URL path');
      return (
        <QuickCashGame 
          isPreview={false}
          gameState={gameState}
          onClose={onPlay} // Use onPlay as the onClose handler
        />
      );
    }
    
    if (lastPart === 'jackpot_millions') {
      console.log('ScratchCardDisplay - Using standalone JackpotMillionsGame from URL path');
      return (
        <JackpotMillionsGame 
          isPreview={false}
          gameState={gameState}
          onClose={onPlay} // Use onPlay as the onClose handler
        />
      );
    }
    
    if (lastPart === 'mega_millions' || lastPart === 'quick_cash_premium') {
      console.log('ScratchCardDisplay - Using MegaMillionsGame from URL path');
      return (
        <MegaMillionsGame 
          isPreview={false}
          gameState={gameState}
          onClose={onPlay} // Use onPlay as the onClose handler
        />
      );
    }
  }
  
  // Check for custom game in URL path
  if (isModularProfile && lastPart && lastPart.startsWith('game_')) {
    console.log('DEBUG ScratchCardDisplay - Found custom game ID in URL path:', lastPart);
    
    // Force a fresh read from localStorage to ensure we have the latest data
    try {
      const rawData = localStorage.getItem('userCreatedGames');
      console.log('DEBUG ScratchCardDisplay - Raw localStorage data for custom game check:', rawData);
      
      const freshGames = JSON.parse(rawData || '[]');
      console.log('DEBUG ScratchCardDisplay - Parsed custom games for URL check:', freshGames);
      
      // Try to find the game by ID
      const customGame = freshGames.find(g => g.id === lastPart);
      
      if (customGame) {
        console.log('DEBUG ScratchCardDisplay - Found custom game from URL path:', customGame.title);
        return (
          <CustomGameCard 
            gameConfig={customGame}
            isPreview={false}
            onPlay={onPlay}
            onBuy={onBuy}
          />
        );
      } else {
        console.warn('DEBUG ScratchCardDisplay - Custom game not found in localStorage with ID:', lastPart);
        
        // As a fallback, try to get the game directly by its ID
        const directGameData = localStorage.getItem(lastPart);
        if (directGameData) {
          try {
            const directGame = JSON.parse(directGameData);
            console.log('DEBUG ScratchCardDisplay - Found game directly by ID:', directGame.title);
            return (
              <CustomGameCard 
                gameConfig={directGame}
                isPreview={false}
                onPlay={onPlay}
                onBuy={onBuy}
              />
            );
          } catch (e) {
            console.error('DEBUG ScratchCardDisplay - Error parsing direct game data:', e);
          }
        }
      }
    } catch (error) {
      console.error('DEBUG ScratchCardDisplay - Error checking for custom game:', error);
    }
  }
  
  // Normalize the cardType to ensure consistent comparison
  const normalizedCardType = String(cardType).toLowerCase().trim();
  
  // Check if this is a custom game ID
  if (normalizedCardType.startsWith('game_') && selectedCustomGame) {
    console.log('ScratchCardDisplay - Rendering custom game:', selectedCustomGame.title);
    return (
      <CustomGameCard 
        gameConfig={selectedCustomGame}
        isPreview={isPreview}
        onPlay={onPlay}
        onBuy={onBuy}
      />
    );
  }
  
  // Choose the appropriate card component based on normalized cardType
  switch(normalizedCardType) {
    case 'quick_cash':
      // In preview mode, just show the card
      // In gameplay mode (when not in preview), use the standalone game component
      if (isPreview) {
        return (
          <QuickCashCard 
            isPreview={true}
            onPlay={onPlay}
            onBuy={onBuy}
            gameState={gameState}
          />
        );
      } else if (currentPath.includes('/scratch-cards/play/quick_cash')) {
        // We're on the gameplay page for Quick Cash
        console.log('ScratchCardDisplay - Using standalone QuickCashGame');
        return (
          <QuickCashGame 
            isPreview={false}
            gameState={gameState}
          />
        );
      } else {
        // Default case, just show the card
        return (
          <QuickCashCard 
            isPreview={isPreview}
            onPlay={onPlay}
            onBuy={onBuy}
            gameState={gameState}
          />
        );
      }
    case 'cash_king':
      // In preview mode, just show the card
      // In gameplay mode (when not in preview), use the standalone game component
      if (isPreview) {
        console.log('Rendering CashKingCard preview component');
        return (
          <CashKingCard 
            isPreview={true}
            onPlay={onPlay}
            onBuy={onBuy}
            gameState={gameState}
          />
        );
      } else if (currentPath.includes('/scratch-cards/play/cash_king')) {
        // We're on the gameplay page for Cash King
        console.log('ScratchCardDisplay - Using standalone CashKingGame');
        return (
          <CashKingGame 
            isPreview={false}
            gameState={gameState}
          />
        );
      } else {
        // Default case, just show the card
        console.log('Rendering CashKingCard component');
        return (
          <CashKingCard 
            isPreview={isPreview}
            onPlay={onPlay}
            onBuy={onBuy}
            onReveal={onReveal}
            gameState={gameState}
          />
        );
      }
    case 'jackpot_millions':
      // In preview mode, just show the card
      // In gameplay mode (when not in preview), use the standalone game component
      if (isPreview) {
        console.log('Rendering JackpotMillionsCard preview component');
        return (
          <JackpotMillionsCard 
            isPreview={true}
            onPlay={onPlay}
            onBuy={onBuy}
            gameState={gameState}
          />
        );
      } else if (currentPath.includes('/scratch-cards/play/jackpot_millions') || 
                 currentPath.includes('/modular-profile') || 
                 currentPath.includes('?cardType=jackpot_millions')) {
        // We're on the gameplay page for Jackpot Millions or modular profile
        console.log('ScratchCardDisplay - Using standalone JackpotMillionsGame');
        return (
          <JackpotMillionsGame 
            isPreview={false}
            gameState={gameState}
            onBuy={onBuy}
            onReveal={onReveal}
          />
        );
      } else {
        // Default case, just show the card
        console.log('Rendering JackpotMillionsCard component');
        return (
          <JackpotMillionsCard 
            isPreview={isPreview}
            onPlay={onPlay}
            onBuy={onBuy}
            onReveal={onReveal}
            gameState={gameState}
          />
        );
      }
    case 'quick_cash_premium':
    case 'mega_millions':
      // In preview mode, just show the card
      // In gameplay mode (when not in preview), use the standalone game component
      if (isPreview) {
        console.log('Rendering MegaMillionsCard preview component');
        return (
          <MegaMillionsCard 
            isPreview={true}
            onPlay={onPlay}
            onBuy={onBuy}
            gameState={gameState}
          />
        );
      } else if (currentPath.includes('/scratch-cards/play/mega_millions') || 
                 currentPath.includes('/modular-profile') || 
                 currentPath.includes('?cardType=quick_cash_premium')) {
        // We're on the gameplay page for Mega Millions or modular profile
        console.log('ScratchCardDisplay - Using MegaMillionsCard with lime green theme');
        return (
          <MegaMillionsCard 
            isPreview={false}
            gameState={gameState}
            onBuy={onBuy}
            onReveal={onReveal}
          />
        );
      } else {
        // Default case, just show the card
        console.log('Rendering MegaMillionsCard component');
        return (
          <MegaMillionsCard 
            isPreview={isPreview}
            onPlay={onPlay}
            onBuy={onBuy}
            onReveal={onReveal}
            gameState={gameState}
          />
        );
      }
    default:
      console.warn(`Unknown card type: ${normalizedCardType}`);
      return null;
  }
};

ScratchCardDisplay.propTypes = {
  cardType: PropTypes.string.isRequired,
  isPreview: PropTypes.bool,
  onPlay: PropTypes.func,
  onBuy: PropTypes.func,
  onReveal: PropTypes.func,
  gameState: PropTypes.object
};

export default ScratchCardDisplay;
